import React, {useEffect, useMemo, useState} from "react";
import Select from "react-select";
import alertService from "../../_services/cart/alert.service";
import tiendaService from "../../_services/cart/tienda.service";

/**
 * Un tipo que sugirio la IA, con lo necesario para decidir: aprobarlo (corrigiendo nombre o
 * categoria), fusionarlo con uno existente si es un sinonimo, o descartarlo si nadie lo usa.
 */
const FilaSugerido = ({sugerido, opciones, tiposActivos, alResolver}) => {
    const [nombre, setNombre] = useState(sugerido.nombre);
    const [categoriaId, setCategoriaId] = useState(sugerido.categoriaId);
    const [destino, setDestino] = useState(null);
    const [ocupado, setOcupado] = useState(false);

    const accion = (promesa) => {
        setOcupado(true);
        promesa
            .then(() => alResolver())
            .catch((err) => alertService.error(err))
            .finally(() => setOcupado(false));
    };

    return (
        <tr>
            <td>
                <input type="text" value={nombre} onChange={(e) => setNombre(e.target.value)}/>
                {sugerido.contexto && <small className="categorias-admin__contexto">«{sugerido.contexto}»</small>}
            </td>
            <td className="categorias-admin__select">
                <Select
                    placeholder="Categoría"
                    value={opciones.find((o) => o.value === categoriaId) || null}
                    options={opciones}
                    onChange={(o) => setCategoriaId(o ? o.value : null)}
                    menuPortalTarget={document.body}
                />
            </td>
            <td>
                {sugerido.sugeridoPor || "—"}
                <small className="categorias-admin__contexto">
                    {sugerido.sugeridoInstant ? new Date(sugerido.sugeridoInstant).toLocaleDateString("es-AR") : ""}
                </small>
            </td>
            <td>{sugerido.publicaciones}</td>
            <td>
                <div className="categorias-admin__acciones">
                    <button type="button" className="button" data-type="primary-small" disabled={ocupado || !categoriaId}
                            onClick={() => accion(tiendaService.adminAprobarSugerido(sugerido.id, nombre, categoriaId))}>
                        Aprobar
                    </button>
                    <div className="categorias-admin__fusion">
                        <Select
                            placeholder="Es lo mismo que…"
                            value={destino}
                            options={tiposActivos}
                            onChange={setDestino}
                            menuPortalTarget={document.body}
                        />
                        <button type="button" className="button" data-type="complementary-small" disabled={ocupado || !destino}
                                title="Las publicaciones pasan a ese tipo y este se borra"
                                onClick={() => accion(tiendaService.adminFusionarSugerido(sugerido.id, destino.value))}>
                            Fusionar
                        </button>
                    </div>
                    <button type="button" className="button" data-type="complementary-small"
                            disabled={ocupado || sugerido.publicaciones > 0}
                            title={sugerido.publicaciones > 0 ? "Tiene publicaciones: fusionalo con uno existente" : "Borrarlo"}
                            onClick={() => accion(tiendaService.adminDescartarSugerido(sugerido.id))}>
                        Descartar
                    </button>
                </div>
            </td>
        </tr>
    );
};

/**
 * Admin > Tipos > Categorías: las categorias generales (Carroceria, Motor, Frenos, Accesorios...) y que
 * tipo de repuesto va en cada una. Cada tipo esta en una sola; lo que quedo sin agrupar se filtra
 * con "Sin categoría". Tambien se dan de alta tipos nuevos, que hasta ahora no tenian ABM.
 */
export const CategoriasAdmin = () => {
    const [categorias, setCategorias] = useState([]);
    const [tipos, setTipos] = useState([]);
    const [filtro, setFiltro] = useState("todas");
    const [texto, setTexto] = useState("");
    const [nuevaCategoria, setNuevaCategoria] = useState("");
    const [nuevoTipo, setNuevoTipo] = useState("");
    const [sugeridos, setSugeridos] = useState([]);

    const cargar = () =>
        tiendaService.adminCategorias()
            .then((r) => {
                setCategorias(r.categorias || []);
                setTipos(r.tipos || []);
            })
            .catch((err) => alertService.error(err));

    const cargarSugeridos = () =>
        tiendaService.adminTiposSugeridos()
            .then(setSugeridos)
            .catch((err) => alertService.error(err));

    /** Resolver un sugerido cambia tambien la tabla de tipos (se activa, o desaparece al fusionar). */
    const alResolverSugerido = () => {
        cargarSugeridos();
        cargar();
    };

    useEffect(() => {
        cargarSugeridos();
        cargar();
    }, []);

    const tiposActivos = useMemo(() => tipos.filter((t) => t.active).map((t) => ({value: t.id, label: t.nombre})), [tipos]);

    const opciones = useMemo(() => categorias.map((c) => ({value: c.id, label: c.nombre + (c.activa ? "" : " (inactiva)")})), [categorias]);

    const guardarTipo = (tipo, cambios) =>
        tiendaService.adminGuardarTipo({
            id: tipo.id,
            categoriaId: tipo.categoriaId ?? null,
            active: tipo.active,
            soloUsados: tipo.soloUsados,
            ...cambios,
        })
            .then((t) => setTipos((ts) => ts.map((x) => (x.id === t.id ? t : x))))
            .catch((err) => alertService.error(err));

    const guardarCategoria = (categoria, cambios) =>
        tiendaService.adminGuardarCategoria({...categoria, ...cambios})
            .then(() => cargar())
            .catch((err) => alertService.error(err));

    const crearCategoria = () => {
        if (!nuevaCategoria.trim()) return;
        tiendaService.adminGuardarCategoria({nombre: nuevaCategoria, orden: categorias.length + 1, activa: true})
            .then(() => {
                setNuevaCategoria("");
                cargar();
            })
            .catch((err) => alertService.error(err));
    };

    const crearTipo = () => {
        if (!nuevoTipo.trim()) return;
        tiendaService.adminGuardarTipo({
            nombre: nuevoTipo,
            categoriaId: typeof filtro === "number" ? filtro : null,
        })
            .then(() => {
                setNuevoTipo("");
                cargar();
            })
            .catch((err) => alertService.error(err));
    };

    const visibles = tipos.filter((t) => {
        if (filtro === "sin" && t.categoriaId) return false;
        if (typeof filtro === "number" && t.categoriaId !== filtro) return false;
        return !texto || t.nombre.toLowerCase().includes(texto.toLowerCase());
    });

    const sinCategoria = tipos.filter((t) => !t.categoriaId).length;

    return (
        <div className="categorias-admin">
            {sugeridos.length > 0 && (
                <section className="cuenta-usuarios__card">
                    <p className="cuenta-usuarios__eyebrow">Tipos sugeridos por la IA ({sugeridos.length})</p>
                    <p className="categorias-admin__ayuda">
                        Los propuso el asistente al cargar publicaciones y el vendedor los aceptó. Mientras están
                        acá no aparecen en filtros ni en SEO, pero las publicaciones que los usan se venden igual.
                        Si es un sinónimo de uno que ya existe, fusionalo: así no quedan duplicados.
                    </p>
                    <table className="cuenta-usuarios__tabla">
                        <thead>
                        <tr>
                            <th>Tipo sugerido</th>
                            <th>Categoría</th>
                            <th>Lo sugirió</th>
                            <th>Publicaciones</th>
                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        {sugeridos.map((s) => (
                            <FilaSugerido key={s.id} sugerido={s} opciones={opciones}
                                          tiposActivos={tiposActivos} alResolver={alResolverSugerido}/>
                        ))}
                        </tbody>
                    </table>
                </section>
            )}

            <section className="cuenta-usuarios__card">
                <p className="cuenta-usuarios__eyebrow">Categorías</p>
                <table className="cuenta-usuarios__tabla">
                    <thead>
                    <tr>
                        <th>Nombre</th>
                        <th>Orden</th>
                        <th>Tipos</th>
                        <th>Activa</th>
                    </tr>
                    </thead>
                    <tbody>
                    {categorias.map((c) => (
                        <tr key={c.id}>
                            <td>
                                <input type="text" defaultValue={c.nombre}
                                       onBlur={(e) => e.target.value.trim() !== c.nombre && guardarCategoria(c, {nombre: e.target.value})}/>
                            </td>
                            <td>
                                <input type="number" defaultValue={c.orden} className="categorias-admin__orden"
                                       onBlur={(e) => Number(e.target.value) !== c.orden && guardarCategoria(c, {orden: Number(e.target.value)})}/>
                            </td>
                            <td>
                                <button type="button" className="categorias-admin__link" onClick={() => setFiltro(c.id)}>
                                    {tipos.filter((t) => t.categoriaId === c.id).length}
                                </button>
                            </td>
                            <td>
                                <input type="checkbox" checked={!!c.activa} onChange={(e) => guardarCategoria(c, {activa: e.target.checked})}/>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
                <div className="categorias-admin__alta">
                    <input type="text" placeholder="Nueva categoría" value={nuevaCategoria}
                           onChange={(e) => setNuevaCategoria(e.target.value)}/>
                    <button type="button" className="button" data-type="primary-small" onClick={crearCategoria}>Agregar</button>
                </div>
            </section>

            <section className="cuenta-usuarios__card">
                <p className="cuenta-usuarios__eyebrow">Tipos de repuesto</p>
                <div className="categorias-admin__filtros">
                    <button type="button" className="button" data-type={filtro === "todas" ? "primary-small" : "complementary-small"}
                            onClick={() => setFiltro("todas")}>Todos ({tipos.length})</button>
                    <button type="button" className="button" data-type={filtro === "sin" ? "primary-small" : "complementary-small"}
                            onClick={() => setFiltro("sin")}>Sin categoría ({sinCategoria})</button>
                    {categorias.map((c) => (
                        <button key={c.id} type="button" className="button"
                                data-type={filtro === c.id ? "primary-small" : "complementary-small"}
                                onClick={() => setFiltro(c.id)}>{c.nombre}</button>
                    ))}
                    <input type="text" placeholder="Buscar tipo" value={texto} onChange={(e) => setTexto(e.target.value)}/>
                </div>

                <table className="cuenta-usuarios__tabla">
                    <thead>
                    <tr>
                        <th>Tipo</th>
                        <th>Categoría</th>
                        <th title="Solo lo publican las cuentas de usados (desarmaderos)">Solo usados</th>
                        <th>Activo</th>
                    </tr>
                    </thead>
                    <tbody>
                    {visibles.map((t) => (
                        <tr key={t.id}>
                            <td>{t.nombre}{t.pendiente && <small className="categorias-admin__contexto">en revisión</small>}</td>
                            <td className="categorias-admin__select">
                                <Select
                                    isClearable
                                    placeholder="Sin categoría"
                                    value={opciones.find((o) => o.value === t.categoriaId) || null}
                                    options={opciones}
                                    onChange={(o) => guardarTipo(t, {categoriaId: o ? o.value : null})}
                                    menuPortalTarget={document.body}
                                />
                            </td>
                            <td>
                                <input type="checkbox" checked={!!t.soloUsados} onChange={(e) => guardarTipo(t, {soloUsados: e.target.checked})}/>
                            </td>
                            <td>
                                <input type="checkbox" checked={!!t.active} onChange={(e) => guardarTipo(t, {active: e.target.checked})}/>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>

                <div className="categorias-admin__alta">
                    <input type="text" placeholder="Nuevo tipo de repuesto" value={nuevoTipo}
                           onChange={(e) => setNuevoTipo(e.target.value)}/>
                    <button type="button" className="button" data-type="primary-small" onClick={crearTipo}>
                        Agregar{typeof filtro === "number" ? " a " + (categorias.find((c) => c.id === filtro)?.nombre || "") : ""}
                    </button>
                </div>
            </section>
        </div>
    );
};
